import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import SiteHeader from '../../shared/SiteHeader';
import AdSense from '../../shared/AdSense';
import Conversation from '../../shared/Conversation';
import type { ChatMessageDto } from '../../types/ChatMessageDto';
import adultData from '../../data/conversations/adult.json';
import childData from '../../data/conversations/child.json';
import prayerForPatienceData from '../../data/conversations/prayer-for-patience.json';
import seniorData from '../../data/conversations/senior.json';
import teenData from '../../data/conversations/teen.json';
import themeOfJamesData from '../../data/conversations/theme-of-james.json';
import yokeAndBurdenData from '../../data/conversations/yoke-and-burden.json';
import { isAdsEnabled } from '../../utils/featureFlags';
import './SampleChat.css';

interface ConversationData {
  conversationId: string;
  name: string;
  userId: string;
  timestamp: string;
  messages: ChatMessageDto[];
}

const CONVERSATIONS: Record<string, ConversationData> = {
  adult: adultData as unknown as ConversationData,
  child: childData as unknown as ConversationData,
  'prayer-for-patience': prayerForPatienceData as unknown as ConversationData,
  senior: seniorData as unknown as ConversationData,
  teen: teenData as unknown as ConversationData,
  'theme-of-james': themeOfJamesData as unknown as ConversationData,
  'yoke-and-burden': yokeAndBurdenData as unknown as ConversationData,
};

const DEFAULT_SLUG = 'adult';

const SampleChat: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const conversation = CONVERSATIONS[slug ?? ''];

  useEffect(() => {
    if (!conversation) {
      return;
    }
    document.title = `Walk It With Me - ${conversation.name}`;
  }, [conversation, slug]);

  if (!conversation) {
    return <Navigate to={`/sample/chat/${DEFAULT_SLUG}`} replace />;
  }

  return (
    <>
      <SiteHeader />
      <main className="container py-4">
        <div className="row">
          <div className="col-12 col-lg-9">
            <h1 className="h3 mb-1">Sample Chat</h1>
            <p className="text-muted mb-4">
              Here's an example of a chat conversation with Walk It With Me.
            </p>

            <Conversation
              messages={conversation.messages}
              showFeedback={false}
              autoScroll={false}
            />

            {/* Horizontal ad — small/medium screens only */}
            {isAdsEnabled() && (
              <div className="d-block d-lg-none my-2 sample-ad-inline">
                <AdSense
                  adClient={process.env.REACT_APP_ADSENSE_CLIENT_ID || ''}
                  adSlot={process.env.REACT_APP_ADSENSE_SLOT_HORIZONTAL || ''}
                  adFormat="horizontal"
                  fullWidthResponsive={true}
                />
              </div>
            )}

            <div className="alert alert-secondary text-center" role="note">
              <p className="mb-2 fw-semibold">Ready to explore the Bible for yourself?</p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Link to="/login" className="btn btn-primary">
                  Log In
                </Link>
                <Link to="/login#signup" className="btn btn-outline-primary">
                  Create a Free Account
                </Link>
              </div>
            </div>
          </div>
          {/* Vertical ad sidebar — large screens only */}
          <div className="col-lg-3 d-none d-lg-flex flex-column sample-ad-sidebar">
            {isAdsEnabled() && (
              <AdSense
                adClient={process.env.REACT_APP_ADSENSE_CLIENT_ID || ''}
                adSlot={process.env.REACT_APP_ADSENSE_SLOT_VERTICAL || ''}
                adFormat="auto"
              />
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default SampleChat;
